import { CommentOutlined, EditOutlined } from '@ant-design/icons';
import { ComunicazioneDTO } from '@framework/api-client';
import { useTranslation } from '@framework/i18n';
import { ButtonDeleteConfirm } from '@framework/ui';
import { Button, Space } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import { useCommunicationsContext } from './context/CommunicationsContext';
import { CommunicationActorsView } from './detail/CommunicationActorsView';
import { CommunicationAttachments } from './detail/CommunicationAttachments';
import { CommunicationCollapseHeader } from './detail/CommunicationCollapseHeader';
import { CommunicationContainer } from './detail/CommunicationContainer';
import { CommunicationSenderProtocol } from './detail/CommunicationSenderProtocol';
import { UserAvatar } from './detail/UserAvatar';
import { CommunicationLogic } from './logic/CommunicationLogic';
import { ICommonCommunicationProps } from './logic/ICommonCommunicationProps';

interface CommunicationItemProps
  extends Pick<
    ICommonCommunicationProps,
    'deleteDraft' | 'getDownloadAttachmentQueryOptions'
  > {
  communication?: ComunicazioneDTO | null;
  isCurrentUser?: boolean;
  isPreview?: boolean;
  isEditable?: boolean;
  onOpen?: (communication: ComunicazioneDTO) => void;
  /**
   * Se valorizzata, mostra il pulsante "Rispondi" sulle comunicazioni che
   * hanno `flAbilitaRisposta`. Usata solo lato frontoffice.
   */
  onReply?: (communication: ComunicazioneDTO) => void;
}

/**
 * Singola comunicazione
 */
export function CommunicationItem(props: CommunicationItemProps) {
  const {
    communication,
    isCurrentUser,
    isPreview,
    isEditable = true,
    onOpen,
    onReply,
    deleteDraft,
    getDownloadAttachmentQueryOptions,
  } = props;
  const { t } = useTranslation();
  const { procedureId, parentProcedureId, isProcedureOpen } =
    useCommunicationsContext();
  const [isDeleting, setIsDeleting] = useState(false);

  if (!communication) return null;

  const sender = CommunicationLogic.sender(communication);
  const isDraft = CommunicationLogic.isDraft(communication);
  const isManual = !!communication.flCaricataManualmente;

  const canBeEdited =
    (isDraft && isEditable) ||
    (isManual &&
      isProcedureOpen &&
      // TODO. È un po' un hack, ma per ora non abbiamo un modo migliore per
      // riconoscere se stiamo vedendo la comunicazione come "Padre" o come "Endoprocedimento".
      // Assumiamo che sia "Padre" se siamo in backoffice e non c'è un altro
      // procedimento padre.
      procedureId != null &&
      parentProcedureId == null);

  const avatar = !isPreview && (
    <UserAvatar isCurrentUser={isCurrentUser} name={sender?.denominazione} />
  );

  const canReply = !!communication.flAbilitaRisposta && !!onReply;

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    onOpen?.(communication);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleReply = (e: React.MouseEvent<HTMLButtonElement>) => {
    onReply?.(communication);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDeleteDraft = async () => {
    setIsDeleting(true);
    await deleteDraft?.(communication.id!);
    setIsDeleting(false);
  };

  return (
    <CommunicationContainer
      isPreview={isPreview}
      isDraft={isDraft}
      avatar={avatar}
      isCurrentUser={isCurrentUser}
      header={
        <CommunicationCollapseHeader
          isDraft={isDraft}
          isManual={isManual}
          date={communication.dataInvio}
          subject={communication.oggetto}
          protocol={
            <CommunicationSenderProtocol communication={communication} />
          }
          actors={
            !isPreview && (
              <CommunicationActorsView communication={communication} />
            )
          }
          actions={
            (!!canBeEdited || canReply) && (
              <Space>
                {canBeEdited && (
                  <>
                    <Button
                      icon={<EditOutlined />}
                      size={'middle'}
                      type={'primary'}
                      onClick={handleOpen}
                    >
                      {isManual
                        ? t('communication-item.edit', 'Modifica')
                        : t(
                            'communication-item.edit-and-send',
                            'Modifica e Invia',
                          )}
                    </Button>
                    <ButtonDeleteConfirm
                      size="middle"
                      iconOnlyButton
                      buttonType="default"
                      buttonDanger
                      loading={isDeleting}
                      onConfirm={() => {
                        handleDeleteDraft();
                      }}
                    />
                  </>
                )}
                {canReply && (
                  <Button
                    icon={<CommentOutlined />}
                    size={'middle'}
                    type={'primary'}
                    onClick={handleReply}
                  >
                    {t('communication-item.reply', 'Rispondi')}
                  </Button>
                )}
              </Space>
            )
          }
        />
      }
      subject={communication.oggetto}
      message={communication.messaggio}
      attachments={
        <CommunicationAttachments
          communication={communication}
          getDownloadAttachmentQueryOptions={getDownloadAttachmentQueryOptions}
        />
      }
    />
  );
}
